import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Stack,
  Group,
  Text,
  Select,
  Checkbox,
  Button,
  Divider,
  ScrollArea,
  Box,
  Card,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useApp } from "../../context/AppContext";
import { Employee } from "../../types/salon";
import { StaffTier, TierReassignment } from "../../types/staffTier";
import { staffLabel } from "./StaffTierMembersCell";
import { TIER_SYNC_COPY, possessive } from "../../utils/staffTierSyncCopy";

const NO_TIER = "__none__";

interface MemberChoice {
  target: string; // tier id, or NO_TIER
  applyService: boolean;
  applyCommission: boolean;
}

interface DeleteTierModalProps {
  opened: boolean;
  onClose: () => void;
  tier: StaffTier;
  tiers: StaffTier[];
  members: Employee[];
  onDeleted: () => void;
}

/**
 * Delete a tier that still has staff. Every member must be reassigned first —
 * to another tier (optionally applying that tier's defaults) or unparented
 * (left untouched). Backed by the transactional reassign-and-delete endpoint.
 */
const DeleteTierModal: React.FC<DeleteTierModalProps> = ({
  opened,
  onClose,
  tier,
  tiers,
  members,
  onDeleted,
}) => {
  const isMobile = useMediaQuery("(max-width: 48em)");
  const { reassignAndDeleteTier } = useApp();

  const targetTiers = useMemo(
    () => tiers.filter((t) => t.id !== tier.id),
    [tiers, tier.id],
  );
  const tierById = useMemo(() => new Map(tiers.map((t) => [t.id, t])), [tiers]);

  const [choices, setChoices] = useState<Record<string, MemberChoice>>({});
  const [acknowledged, setAcknowledged] = useState(false);
  const [setAllTarget, setSetAllTarget] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      const init: Record<string, MemberChoice> = {};
      members.forEach((m) => {
        init[m.id] = {
          target: NO_TIER,
          applyService: false,
          applyCommission: false,
        };
      });
      setChoices(init);
      setAcknowledged(false);
      setSetAllTarget(null);
      setError(null);
    }
  }, [opened, members]);

  const patch = (staffId: string, next: Partial<MemberChoice>): void =>
    setChoices((prev) => ({
      ...prev,
      [staffId]: { ...prev[staffId], ...next },
    }));

  const applyToAll = (target: string | null): void => {
    setSetAllTarget(target);
    if (!target) return;
    setChoices((prev) => {
      const next: Record<string, MemberChoice> = {};
      Object.keys(prev).forEach((id) => {
        next[id] = { target, applyService: false, applyCommission: false };
      });
      return next;
    });
  };

  const targetSelectData = [
    { value: NO_TIER, label: "No tier (unparent)" },
    ...targetTiers.map((t) => ({ value: t.id, label: t.name })),
  ];

  const handleDelete = async (): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      const reassignments: TierReassignment[] = members.map((m) => {
        const choice = choices[m.id];
        const dest =
          choice.target === NO_TIER ? undefined : tierById.get(choice.target);
        return {
          staff_id: m.id,
          target_tier_id: dest ? dest.id : null,
          apply_service:
            !!dest && dest.has_service_defaults && choice.applyService,
          apply_commission:
            !!dest && dest.has_commission_defaults && choice.applyCommission,
        };
      });
      await reassignAndDeleteTier(tier.id, {
        acknowledge: true,
        reassignments,
      });
      onDeleted();
    } catch (e) {
      console.error("Error deleting tier with reassignment:", e);
      setError(
        "Couldn't delete the tier. Please review the choices and retry.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Delete "${tier.name}"`}
      centered
      size="xl"
      fullScreen={isMobile}
    >
      <Stack>
        <Text size="sm" c="dimmed">
          Reassign each staff member in this tier to a different tier or
          unparent them entirely. Unparenting leaves their services and
          commissions exactly as they are.
        </Text>

        {targetTiers.length > 0 && (
          <Group align="flex-end" gap="sm">
            <Select
              label="Set all to"
              placeholder="Choose a destination for everyone"
              data={targetSelectData}
              value={setAllTarget}
              onChange={applyToAll}
              w={280}
              comboboxProps={{ withinPortal: true }}
            />
          </Group>
        )}

        <Divider />

        <ScrollArea.Autosize mah={360}>
          <Stack gap="sm">
            {members.map((member) => {
              const choice = choices[member.id];
              if (!choice) return null;
              const dest =
                choice.target === NO_TIER
                  ? undefined
                  : tierById.get(choice.target);
              return (
                <Card key={member.id} withBorder radius="md" p="sm">
                  <Group justify="space-between" wrap="wrap" gap="sm">
                    <Text size="sm" fw={500}>
                      {staffLabel(member)}
                    </Text>
                    <Select
                      aria-label={`Reassign ${staffLabel(member)}`}
                      data={targetSelectData}
                      value={choice.target}
                      onChange={(v) =>
                        patch(member.id, {
                          target: v || NO_TIER,
                          applyService: false,
                          applyCommission: false,
                        })
                      }
                      w={220}
                      comboboxProps={{ withinPortal: true }}
                    />
                  </Group>
                  {dest &&
                    (dest.has_service_defaults ||
                      dest.has_commission_defaults) && (
                      <Stack gap={4} mt="xs">
                        {dest.has_service_defaults && (
                          <Checkbox
                            size="sm"
                            checked={choice.applyService}
                            onChange={(e) =>
                              patch(member.id, {
                                applyService: e.currentTarget.checked,
                              })
                            }
                            label={TIER_SYNC_COPY.serviceOptionLabel(
                              possessive(staffLabel(member)),
                            )}
                          />
                        )}
                        {dest.has_commission_defaults && (
                          <Checkbox
                            size="sm"
                            checked={choice.applyCommission}
                            onChange={(e) =>
                              patch(member.id, {
                                applyCommission: e.currentTarget.checked,
                              })
                            }
                            label={TIER_SYNC_COPY.commissionOptionLabel(
                              possessive(staffLabel(member)),
                            )}
                          />
                        )}
                      </Stack>
                    )}
                </Card>
              );
            })}
          </Stack>
        </ScrollArea.Autosize>

        {error && (
          <Text c="red" size="sm">
            {error}
          </Text>
        )}

        <Checkbox
          color="red"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.currentTarget.checked)}
          label="I understand this deletes the tier and permanently applies the choices above."
        />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Box>
            <Button
              color="red"
              disabled={!acknowledged || submitting}
              loading={submitting}
              onClick={handleDelete}
            >
              Delete tier &amp; reassign staff
            </Button>
          </Box>
        </Group>
      </Stack>
    </Modal>
  );
};

export default DeleteTierModal;
